import React, { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useParams } from "react-router-dom";
import { useNavigateWithCommandClick } from "../../hooks/useNavigateWithCommandClick";

import Button from "../../components/Button";
import { Table, Row, Column, ActionsColumn } from "../../components/Table";
import { LoadingState, EmptyState } from "../../components/CommonStates";
import Modal, { ModalTitle, ModalActions } from "../../components/Modal";
import { Form, FormGroup, FormRow, Label } from "../../components/Form";
import { Input } from "../../components/Input";
import { TextArea } from "../../components/Textarea";
import {
  CheckboxGroup,
  Checkbox,
  CheckboxLabel,
  CheckboxDescription
} from "../../components/Checkbox";
import BotDisplay from "../../components/BotDisplay";
import { StatusBadge } from "../../components/StatusBadge";
import { Header } from "../../components/AdminTab";
import { Dropdown } from "../../components/Dropdown";

function BotsTab({ onError, onSuccess, onBotCreated }) {
  const { filter } = useParams();
  const navigate = useNavigateWithCommandClick();
  const [bots, setBots] = useState([]);
  const [displayedBots, setDisplayedBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState(filter || "");
  const [editingBot, setEditingBot] = useState(null);
  const [updatingBot, setUpdatingBot] = useState(false);
  const [formData, setFormData] = useState({});
  const [botTypes, setBotTypes] = useState([]);
  const [botProviders, setBotProviders] = useState([]);
  const [creatingBot, setCreatingBot] = useState(false);
  const [llamaCloudIndexes, setLlamaCloudIndexes] = useState([]);

  const fetchBots = useCallback(async () => {
    setLoading(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminBots, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setBots(data.bots || []);
      } else {
        onError(data.error || "Failed to fetch bots");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // Filter bots based on selected filter
  const filterBots = useCallback((botsToFilter, filter) => {
    if (!filter) return botsToFilter;

    switch (filter) {
      case "public":
        return botsToFilter.filter((bot) => bot.public === true);
      case "private":
        return botsToFilter.filter((bot) => bot.public === false);
      default:
        return botsToFilter;
    }
  }, []);

  // Handle filter changes
  const handleFilterChange = (event) => {
    const newFilter = event.target.value;

    // Don't do anything if the value hasn't actually changed
    if (newFilter === selectedFilter) {
      return;
    }

    // Update selectedFilter immediately
    setSelectedFilter(newFilter);

    // Update URL based on filter selection
    if (newFilter) {
      navigate(`/admin/bots/${newFilter}`, { replace: true });
    } else {
      navigate("/admin/bots", { replace: true });
    }
  };

  const fetchBotTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.adminBots}/types`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const types = await response.json();
        setBotTypes(types);
      } else {
        // Don't show error for bot types, just use empty array
        setBotTypes([]);
      }
    } catch (error) {
      // Don't show error for bot types, just use empty array
      setBotTypes([]);
    }
  }, []);

  const fetchBotProviders = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.adminBots}/providers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const providers = await response.json();
        setBotProviders(providers);
      } else {
        // Don't show error for bot providers, just use empty array
        setBotProviders([]);
      }
    } catch (error) {
      // Don't show error for bot providers, just use empty array
      setBotProviders([]);
    }
  }, []);

  const fetchLlamaCloudIndexes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.data}/llamacloud-indexes`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLlamaCloudIndexes(data.indexes || []);
      } else {
        // Don't show error, just use empty array
        setLlamaCloudIndexes([]);
      }
    } catch (error) {
      // Don't show error, just use empty array
      setLlamaCloudIndexes([]);
    }
  }, []);

  const shouldLockProvider = (botType) => {
    if (!botType) return false;
    const type = botType.toLowerCase();
    return (
      type.includes("openai") ||
      type.includes("open-ai") ||
      type.includes("gpt") ||
      type.includes("claude") ||
      type.includes("anthropic") ||
      type.includes("google") ||
      type.includes("gemini")
    );
  };

  const shouldLockModel = (botType) => {
    return false; // No bot types require locked models anymore
  };

  const getEffectiveProvider = (botType, explicitProvider) => {
    // First check if the bot type locks the provider
    if (botType) {
      const type = botType.toLowerCase();

      if (type.includes("claude") || type.includes("anthropic")) {
        return "anthropic";
      } else if (
        type.includes("openai") ||
        type.includes("open-ai") ||
        type.includes("gpt")
      ) {
        return "openai";
      } else if (type.includes("google") || type.includes("gemini")) {
        return "google";
      }
    }

    // If bot type doesn't lock provider, use explicit provider
    if (explicitProvider) return explicitProvider;

    return null;
  };

  const fetchBotDetails = async (botId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminBot(botId), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.bot;
      } else {
        throw new Error("Failed to fetch bot details");
      }
    } catch (error) {
      console.error("Error fetching bot details:", error);
      throw error;
    }
  };

  const handleEditBot = async (bot) => {
    // Open modal immediately with loading state
    setEditingBot({ id: bot.id, name: bot.name, loading: true });
    setFormData({});

    // Fetch LlamaCloud indexes for dropdown
    fetchLlamaCloudIndexes();

    try {
      const botDetails = await fetchBotDetails(bot.id);
      setEditingBot(botDetails);
      setFormData({
        name: botDetails.name || "",
        description: botDetails.description || "",
        color: botDetails.color || "#4CAF50",
        initialMessage: botDetails.initialMessage || "",
        systemPrompt: botDetails.systemPrompt || "",
        public: botDetails.public || false,
        trackConversationHistory: botDetails.trackConversationHistory || false,
        enableReAsk: botDetails.enableReAsk || false,
        useStructuredOutput: botDetails.useStructuredOutput || false,
        vision: botDetails.vision || false,
        webSearchEnabled: botDetails.webSearchEnabled || false,
        model: botDetails.model || "",
        notes: botDetails.notes || "",
        type: botDetails.type || "",
        provider: botDetails.provider || "",
        documentIndexName: botDetails.documentIndexName || ""
      });
    } catch (error) {
      onError("Failed to load bot details");
      setEditingBot(null); // Close modal on error
    }
  };

  const generateIdFromName = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and dashes
      .replace(/\s+/g, "-") // Replace spaces with dashes
      .replace(/-+/g, "-") // Replace multiple dashes with single dash
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing dashes
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Auto-generate ID from name when creating a bot
      if (field === "name" && creatingBot) {
        newData.id = generateIdFromName(value);
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingBot) return;

    setUpdatingBot(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      // Ensure color is always a valid hex color, default to "#4CAF50" if missing or invalid
      const dataToSend = {
        ...formData,
        color:
          formData.color && formData.color.trim() ? formData.color : "#4CAF50"
      };
      const response = await fetch(API_ENDPOINTS.updateBot(editingBot.id), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess("Bot updated successfully!");
        setEditingBot(null);
        setFormData({});
        fetchBots(); // Refresh the bot list
      } else {
        onError(data.error || "Failed to update bot");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingBot(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingBot(null);
    setFormData({});
    onError("");
  };

  const handleCreateBot = () => {
    setCreatingBot(true);

    // Fetch LlamaCloud indexes for dropdown
    fetchLlamaCloudIndexes();

    setFormData({
      id: "",
      name: "",
      description: "",
      color: "#4CAF50",
      initialMessage: "",
      systemPrompt: "",
      public: false,
      trackConversationHistory: true,
      enableReAsk: false,
      useStructuredOutput: false,
      vision: false,
      webSearchEnabled: false,
      model: "",
      notes: "",
      type: botTypes[0] || "",
      provider: botProviders[0] || "",
      documentIndexName: ""
    });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setUpdatingBot(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      // Ensure color is always a valid hex color, default to "#4CAF50" if missing or invalid
      const dataToSend = {
        ...formData,
        color:
          formData.color && formData.color.trim() ? formData.color : "#4CAF50"
      };
      const response = await fetch(API_ENDPOINTS.adminBots, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess("Bot created successfully!");
        setCreatingBot(false);
        setFormData({});
        fetchBots(); // Refresh the bot list
        // Refresh domain rules and bots list in parent component
        if (onBotCreated) {
          onBotCreated();
        }
      } else {
        onError(data.error || "Failed to create bot");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingBot(false);
    }
  };

  const handleCancelCreate = () => {
    setCreatingBot(false);
    setFormData({});
    onError("");
  };

  // Update selectedFilter when URL changes
  useEffect(() => {
    const urlFilter = filter || "";
    if (urlFilter !== selectedFilter) {
      setSelectedFilter(urlFilter);
    }
  }, [filter, selectedFilter]);

  // Update displayed bots when bots or filter changes
  useEffect(() => {
    const filtered = filterBots(bots, selectedFilter);
    setDisplayedBots(filtered);
  }, [bots, selectedFilter, filterBots]);

  useEffect(() => {
    fetchBots();
    fetchBotTypes();
    fetchBotProviders();
  }, [fetchBots, fetchBotTypes, fetchBotProviders]);

  if (loading && !bots.length) {
    return <LoadingState>Loading bots...</LoadingState>;
  }

  if (bots.length === 0) {
    return (
      <EmptyState>
        <h4>No bots found</h4>
        <p>There are no bots available in the system.</p>
      </EmptyState>
    );
  }

  if (displayedBots.length === 0 && selectedFilter) {
    return (
      <>
        <Header>
          <Dropdown
            id="bot-filter"
            value={selectedFilter}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </Dropdown>
          <Button onClick={handleCreateBot}>Create new bot</Button>
        </Header>
        <EmptyState>
          <h4>No {selectedFilter} bots found</h4>
          <p>
            {selectedFilter === "public"
              ? "There are no public bots available."
              : "There are no private bots available."}
          </p>
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <Header>
        <Dropdown
          id="bot-filter"
          value={selectedFilter}
          onChange={handleFilterChange}
        >
          <option value="">All</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
        </Dropdown>
        <Button onClick={handleCreateBot}>Create new bot</Button>
      </Header>

      <Table columns="1.25fr 1fr 0.25fr 400px">
        <Row>
          <Column>Bot</Column>
          <Column>Type</Column>
          <Column>Status</Column>
          <ActionsColumn>Actions</ActionsColumn>
        </Row>

        {displayedBots.map((bot) => (
          <Row key={bot.id}>
            <Column data-label="Bot">
              <BotDisplay bot={bot} variant="list" showDescription={false} />
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#666",
                  marginTop: "0.25rem",
                  paddingLeft: "2.5rem"
                }}
              >
                {bot.id}
              </div>
            </Column>
            <Column data-label="Type">{bot.type || "Unknown"}</Column>
            <Column data-label="Status">
              <StatusBadge $isPublic={bot.public}>
                {bot.public ? "Public" : "Private"}
              </StatusBadge>
            </Column>
            <ActionsColumn data-label="Actions">
              <Button onClick={() => handleEditBot(bot)}>Edit</Button>
              <Button
                onClick={(event) =>
                  navigate(`/admin/chat-logs/specific-bot/${bot.id}`, {}, event)
                }
                style={{ whiteSpace: "nowrap" }}
              >
                View logs
              </Button>
              <Button
                onClick={(event) => navigate(`/bots/${bot.id}`, {}, event)}
              >
                Chat
              </Button>
            </ActionsColumn>
          </Row>
        ))}
      </Table>

      {/* Edit Bot Modal */}
      <Modal isOpen={!!editingBot} onClose={handleCancelEdit}>
        <ModalTitle>Edit {editingBot?.name}</ModalTitle>

        {editingBot?.loading ? (
          <LoadingState>Loading bot details...</LoadingState>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  disabled={updatingBot}
                  data-1p-ignore
                  required
                  placeholder="Bot name"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  type="color"
                  value={formData.color || "#4CAF50"}
                  onChange={(e) => handleFormChange("color", e.target.value)}
                  disabled={updatingBot}
                  data-1p-ignore
                  style={{ width: "60px", height: "47px", padding: "0" }}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="id">ID</Label>
              <Input
                id="id"
                type="text"
                value={editingBot?.id || ""}
                disabled={true}
                data-1p-ignore
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description</Label>
              <TextArea
                id="description"
                value={formData.description || ""}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                disabled={updatingBot}
                data-1p-ignore
                placeholder="Brief description of the bot's purpose..."
                $size="small"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="type">Type</Label>
              <Dropdown
                id="type"
                value={formData.type || ""}
                onChange={(e) => handleFormChange("type", e.target.value)}
                disabled={updatingBot}
              >
                {botTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Dropdown>
            </FormGroup>

            {(formData.type === "llamacloud-document-index" ||
              formData.type === "llamacloud-reddit-mix") && (
              <FormGroup>
                <Label htmlFor="documentIndexName">Document index</Label>
                <Dropdown
                  id="documentIndexName"
                  value={formData.documentIndexName || ""}
                  onChange={(e) =>
                    handleFormChange("documentIndexName", e.target.value)
                  }
                  disabled={updatingBot}
                >
                  <option value="">Select an index...</option>
                  {llamaCloudIndexes.map((index) => (
                    <option key={index.id} value={index.name}>
                      {index.name}
                    </option>
                  ))}
                </Dropdown>
              </FormGroup>
            )}

            {!shouldLockProvider(formData.type) && (
              <FormGroup>
                <Label htmlFor="provider">Provider</Label>
                <Dropdown
                  id="provider"
                  value={formData.provider || ""}
                  onChange={(e) => handleFormChange("provider", e.target.value)}
                  disabled={updatingBot}
                >
                  {botProviders.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </Dropdown>
              </FormGroup>
            )}

            {!shouldLockModel(formData.type) && (
              <FormGroup>
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  type="text"
                  value={formData.model || ""}
                  onChange={(e) => handleFormChange("model", e.target.value)}
                  disabled={updatingBot}
                  placeholder={
                    formData.type === "claude-custom-context"
                      ? "claude-opus-4-6, claude-sonnet-4-5, etc"
                      : (formData.provider || editingBot?.provider) === "openai"
                      ? "gpt-5, gpt-5-mini, gpt-5-nano, etc"
                      : (formData.provider || editingBot?.provider) ===
                        "anthropic"
                      ? "claude-opus-4-6, claude-sonnet-4-5, etc"
                      : (formData.provider || editingBot?.provider) === "google"
                      ? "gemini-2.5-pro, gemini-2.5-flash, etc"
                      : "gpt-5, gpt-5-mini, gpt-5-nano, etc"
                  }
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="systemPrompt">System prompt</Label>
              <TextArea
                id="systemPrompt"
                value={formData.systemPrompt || ""}
                onChange={(e) =>
                  handleFormChange("systemPrompt", e.target.value)
                }
                disabled={updatingBot}
                placeholder="Instructions for the AI assistant..."
                $size="large"
                style={{ minHeight: "360px" }}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="initialMessage">Initial message</Label>
              <TextArea
                id="initialMessage"
                value={formData.initialMessage || ""}
                onChange={(e) =>
                  handleFormChange("initialMessage", e.target.value)
                }
                disabled={updatingBot}
                placeholder="First message users will see..."
                $size="small"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="notes">
                Internal notes (not visible to non-admin users)
              </Label>
              <TextArea
                id="notes"
                value={formData.notes || ""}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                disabled={updatingBot}
                placeholder="Notes about this bot..."
                $size="small"
              />
            </FormGroup>

            <CheckboxGroup style={{ marginTop: "1rem" }}>
              <Checkbox
                id="public"
                type="checkbox"
                checked={formData.public || false}
                onChange={(e) => handleFormChange("public", e.target.checked)}
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="public">
                Public
                <CheckboxDescription>
                  Allow users in the Propel app to access the bot
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                id="trackConversationHistory"
                type="checkbox"
                checked={formData.trackConversationHistory || false}
                onChange={(e) =>
                  handleFormChange("trackConversationHistory", e.target.checked)
                }
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="trackConversationHistory">
                Track conversation history
                <CheckboxDescription>
                  Send up to the last 10 messages to the model
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                id="enableReAsk"
                type="checkbox"
                checked={formData.enableReAsk || false}
                onChange={(e) =>
                  handleFormChange("enableReAsk", e.target.checked)
                }
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="enableReAsk">
                Allow re-asking
                <CheckboxDescription>
                  Ask the same question to multiple models
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                id="useStructuredOutput"
                type="checkbox"
                checked={formData.useStructuredOutput || false}
                onChange={(e) =>
                  handleFormChange("useStructuredOutput", e.target.checked)
                }
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="useStructuredOutput">
                Use structured output
                <CheckboxDescription>
                  Show citations in the response
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>

            {getEffectiveProvider(
              formData.type || editingBot?.type,
              formData.provider || editingBot?.provider
            ) === "openai" && (
              <CheckboxGroup>
                <Checkbox
                  id="vision"
                  type="checkbox"
                  checked={formData.vision || false}
                  onChange={(e) => handleFormChange("vision", e.target.checked)}
                  disabled={updatingBot}
                />
                <CheckboxLabel htmlFor="vision">
                  Use vision
                  <CheckboxDescription>
                    Only allow images as input
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxGroup>
            )}

            {getEffectiveProvider(
              formData.type || editingBot?.type,
              formData.provider || editingBot?.provider
            ) === "openai" && (
              <CheckboxGroup>
                <Checkbox
                  id="webSearchEnabled"
                  type="checkbox"
                  checked={formData.webSearchEnabled || false}
                  onChange={(e) =>
                    handleFormChange("webSearchEnabled", e.target.checked)
                  }
                  disabled={updatingBot}
                />
                <CheckboxLabel htmlFor="webSearchEnabled">
                  Use web search
                  <CheckboxDescription>
                    Allow the bot to search the web for current information
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxGroup>
            )}

            <ModalActions>
              <Button
                variant="secondary"
                type="button"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updatingBot}>
                {updatingBot ? "Saving..." : "Save"}
              </Button>
            </ModalActions>
          </Form>
        )}
      </Modal>

      {/* Create Bot Modal */}
      <Modal isOpen={creatingBot} onClose={handleCancelCreate}>
        <ModalTitle>Create new bot</ModalTitle>

        <Form onSubmit={handleCreateSubmit}>
          <FormRow>
            <FormGroup>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name || ""}
                onChange={(e) => handleFormChange("name", e.target.value)}
                disabled={updatingBot}
                required
                data-1p-ignore
                placeholder="Bot name"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                type="color"
                value={formData.color || "#4CAF50"}
                onChange={(e) => handleFormChange("color", e.target.value)}
                disabled={updatingBot}
                style={{ width: "60px", height: "47px", padding: "0" }}
                data-1p-ignore
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="id">ID</Label>
            <Input
              id="id"
              type="text"
              value={formData.id || ""}
              onChange={(e) => handleFormChange("id", e.target.value)}
              disabled={true}
              placeholder="Will be auto-generated from bot name"
              required
              data-1p-ignore
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">Description</Label>
            <TextArea
              id="description"
              value={formData.description || ""}
              onChange={(e) => handleFormChange("description", e.target.value)}
              disabled={updatingBot}
              placeholder="Brief description of the bot's purpose..."
              $size="small"
              data-1p-ignore
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="type">Type</Label>
            <Dropdown
              id="type"
              value={formData.type || ""}
              onChange={(e) => handleFormChange("type", e.target.value)}
              disabled={updatingBot}
              required
            >
              {botTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Dropdown>
          </FormGroup>

          {(formData.type === "llamacloud-document-index" ||
            formData.type === "llamacloud-reddit-mix") && (
            <FormGroup>
              <Label htmlFor="documentIndexName">Document index</Label>
              <Dropdown
                id="documentIndexName"
                value={formData.documentIndexName || ""}
                onChange={(e) =>
                  handleFormChange("documentIndexName", e.target.value)
                }
                disabled={updatingBot}
              >
                <option value="">Select an index...</option>
                {llamaCloudIndexes.map((index) => (
                  <option key={index.id} value={index.name}>
                    {index.name}
                  </option>
                ))}
              </Dropdown>
            </FormGroup>
          )}

          {!shouldLockProvider(formData.type) && (
            <FormGroup>
              <Label htmlFor="provider">Provider</Label>
              <Dropdown
                id="provider"
                value={formData.provider || ""}
                onChange={(e) => handleFormChange("provider", e.target.value)}
                disabled={updatingBot}
                required
              >
                {botProviders.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </Dropdown>
            </FormGroup>
          )}

          {!shouldLockModel(formData.type) && (
            <FormGroup>
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                type="text"
                value={formData.model || ""}
                onChange={(e) => handleFormChange("model", e.target.value)}
                disabled={updatingBot}
                placeholder={
                  formData.type === "claude-custom-context"
                    ? "claude-opus-4-6, claude-sonnet-4-5, etc"
                    : formData.provider === "openai"
                    ? "gpt-5, gpt-5-mini, gpt-5-nano, etc"
                    : formData.provider === "anthropic"
                    ? "claude-opus-4-6, claude-sonnet-4-5, etc"
                    : formData.provider === "google"
                    ? "gemini-2.5-pro, gemini-2.5-flash, etc"
                    : "gpt-5, gpt-5-mini, gpt-5-nano, etc"
                }
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label htmlFor="systemPrompt">System prompt</Label>
            <TextArea
              id="systemPrompt"
              value={formData.systemPrompt || ""}
              onChange={(e) => handleFormChange("systemPrompt", e.target.value)}
              disabled={updatingBot}
              placeholder="Instructions for the AI assistant..."
              $size="large"
              style={{ minHeight: "360px" }}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="initialMessage">Initial message</Label>
            <TextArea
              id="initialMessage"
              value={formData.initialMessage || ""}
              onChange={(e) =>
                handleFormChange("initialMessage", e.target.value)
              }
              disabled={updatingBot}
              placeholder="First message users will see..."
              $size="small"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="notes">
              Internal notes (not visible to non-admin users)
            </Label>
            <TextArea
              id="notes"
              value={formData.notes || ""}
              onChange={(e) => handleFormChange("notes", e.target.value)}
              disabled={updatingBot}
              placeholder="Notes about this bot..."
              $size="small"
            />
          </FormGroup>

          <CheckboxGroup style={{ marginTop: "1rem" }}>
            <Checkbox
              id="public"
              type="checkbox"
              checked={formData.public || false}
              onChange={(e) => handleFormChange("public", e.target.checked)}
              disabled={updatingBot}
            />
            <CheckboxLabel htmlFor="public">
              Public
              <CheckboxDescription>
                Allow users in the Propel app to access the bot
              </CheckboxDescription>
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <Checkbox
              id="trackConversationHistory"
              type="checkbox"
              checked={formData.trackConversationHistory || false}
              onChange={(e) =>
                handleFormChange("trackConversationHistory", e.target.checked)
              }
              disabled={updatingBot}
            />
            <CheckboxLabel htmlFor="trackConversationHistory">
              Track conversation history
              <CheckboxDescription>
                Send up to the last 10 messages to the model
              </CheckboxDescription>
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <Checkbox
              id="enableReAsk"
              type="checkbox"
              checked={formData.enableReAsk || false}
              onChange={(e) =>
                handleFormChange("enableReAsk", e.target.checked)
              }
              disabled={updatingBot}
            />
            <CheckboxLabel htmlFor="enableReAsk">
              Allow re-asking
              <CheckboxDescription>
                Ask the same question to multiple models
              </CheckboxDescription>
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <Checkbox
              id="useStructuredOutput"
              type="checkbox"
              checked={formData.useStructuredOutput || false}
              onChange={(e) =>
                handleFormChange("useStructuredOutput", e.target.checked)
              }
              disabled={updatingBot}
            />
            <CheckboxLabel htmlFor="useStructuredOutput">
              Use structured output
              <CheckboxDescription>
                Show citations in the response
              </CheckboxDescription>
            </CheckboxLabel>
          </CheckboxGroup>

          {getEffectiveProvider(formData.type, formData.provider) ===
            "openai" && (
            <CheckboxGroup>
              <Checkbox
                id="vision"
                type="checkbox"
                checked={formData.vision || false}
                onChange={(e) => handleFormChange("vision", e.target.checked)}
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="vision">
                Use vision
                <CheckboxDescription>
                  Only allow images as input
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>
          )}

          {getEffectiveProvider(formData.type, formData.provider) ===
            "openai" && (
            <CheckboxGroup>
              <Checkbox
                id="webSearchEnabled"
                type="checkbox"
                checked={formData.webSearchEnabled || false}
                onChange={(e) =>
                  handleFormChange("webSearchEnabled", e.target.checked)
                }
                disabled={updatingBot}
              />
              <CheckboxLabel htmlFor="webSearchEnabled">
                Use web search
                <CheckboxDescription>
                  Allow the bot to search the web for current information
                </CheckboxDescription>
              </CheckboxLabel>
            </CheckboxGroup>
          )}

          <ModalActions>
            <Button
              variant="secondary"
              type="button"
              onClick={handleCancelCreate}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updatingBot}>
              {updatingBot ? "Creating..." : "Create bot"}
            </Button>
          </ModalActions>
        </Form>
      </Modal>
    </>
  );
}

export default BotsTab;
